import { Injectable, signal } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { PalantirDialogService, PalantirLabelColorsEnum } from '@fieldcontrol/palantir'
import { CreateWarrantyInput, Equipment, WarrantyTemplate } from '@generated/sammathnaur.graphql'
import { TranslateService } from '@ngx-translate/core'
import { parseISO } from 'date-fns'
import { WarrantiesMapperService } from './warranties.mapper.service'
import { WarrantiesService } from '../../../core/api/warranties/warranties.service'
import { WarrantiesDialogComponent } from '../components/warranty-template-dialog/warranties-dialog.component'
import { WARRANTY_TYPE, WarrantyForm } from '../warranties.type'

@Injectable({
  providedIn: 'root',
})
export class MaintenanceTabWarrantiesService {
  public isLoading = signal<boolean>(true)
  public warrantiesForm = signal<FormGroup<WarrantyForm>[]>([])
  public equipments = signal<Equipment[]>([])
  public selectedWarrantyFormIndex = signal<number | null>(null)
  public selectedWarrantyType = signal<WARRANTY_TYPE>(WARRANTY_TYPE.EQUIPMENTS)
  public eventRequests: CreateWarrantyInput[] = []
  public hasWarranties = signal<boolean>(false)
  public hasCreateWarrantyPermission = signal<boolean>(false)
  public hasEditWarrantyPermission = signal<boolean>(false)
  public selectedWarrantyForm?: FormGroup<WarrantyForm>
  public selectedMaintenanceTypeId = signal<string | null | undefined>(null)
  public selectedCompanyId = signal<string | null | undefined>(null)
  public maintenanceId = signal<string | null | undefined>(null)

  public constructor(
    private readonly palantirDialogService: PalantirDialogService,
    private readonly warrantiesMapperService: WarrantiesMapperService,
    private readonly translateService: TranslateService,
    private readonly warrantiesService: WarrantiesService
  ) { }

  public async openDialog(): Promise<void> {
    const dialogRef = this.palantirDialogService.openDialog(WarrantiesDialogComponent, {
      size: 'medium',
      data: {
        selectedMaintenanceTypeId: this.selectedMaintenanceTypeId(),
        selectedCompanyId: this.selectedCompanyId(),
      }
    })

    const result = await dialogRef.afterClosed<{ warrantyTemplate: WarrantyTemplate, coverageStartedAt: string }>()

    if (result?.warrantyTemplate) {
      this.isLoading.set(true)
      const warrantyForm: FormGroup<WarrantyForm> = this.warrantiesMapperService.warrantyTemplateCreateWarrantyForm(result.warrantyTemplate, result.coverageStartedAt, this.selectedCompanyId())
      this.warrantiesForm.set([...this.warrantiesForm(), warrantyForm])
      this.selectedWarrantyFormIndex.set(this.warrantiesForm().length - 1)
      this.eventRequests.push(this.warrantiesMapperService.toCreateInput(warrantyForm))

      if(this.maintenanceId()) {
        const warranty = (await this.warrantiesService.create({
          ...this.warrantiesMapperService.toCreateInput(warrantyForm),
          maintenanceId: this.maintenanceId(),
        })).data?.createWarranty

        const warrantiesForms = this.warrantiesForm()
        const index = this.selectedWarrantyFormIndex()

        if (index === null) return

        warrantiesForms[index].patchValue({ id: warranty?.id })
      }

      this.sendWarrantiesOnEvent()
      this.setHasWarranties()
      this.selectedWarrantyType.set(WARRANTY_TYPE.MAINTENANCES)
      this.selectedWarrantyForm = warrantyForm
      this.isLoading.set(false)
    }
  }

  public sendWarrantiesOnEvent(): void {
    const event = new CustomEvent('warrantiesUpdated', {
      detail: {
        warranties: this.eventRequests,
      }
    })

    window.dispatchEvent(event)
  }

  public mapperTag(warrantyForm: FormGroup<WarrantyForm>) {
    const statusColor = {
      active: 'active',
      expired: 'expired',
      extended: 'extended',
      future: 'future',
      default: 'grey',
    }

    const status = (this.calculateWarrantyStatus(warrantyForm?.controls) ?? 'default') as keyof typeof statusColor
    return {
      color: statusColor[status] as PalantirLabelColorsEnum,
      label: this.translateService.instant(`TAB_WARRANTIES_MAINTENANCE.DIALOG.STATUS.${status.toUpperCase()}`)
    }
  }

  private calculateWarrantyStatus(warranty: WarrantyForm | null): string | null {
    if (!warranty) return null

    const now = new Date()
    now.setHours(0, 0, 0, 0)

    const coverageStartedAt = warranty.coverageStartedAt?.value
      ? parseISO(warranty.coverageStartedAt.value)
      : null

    const coverageEndedAt = warranty.coverageEndedAt?.value
      ? parseISO(warranty.coverageEndedAt.value)
      : null

    const extendedCoverageEndedAt = warranty.extendedCoverageEndedAt?.value
      ? parseISO(warranty.extendedCoverageEndedAt.value)
      : null

    if (coverageStartedAt && coverageStartedAt > now) {
      return 'future'
    }

    if (coverageEndedAt && coverageEndedAt <= now) {
      return extendedCoverageEndedAt && extendedCoverageEndedAt > now ? 'extended' : 'expired'
    }

    if (coverageEndedAt && coverageEndedAt >= now) {
      return 'active'
    }

    return null
  }

  public hasData(): boolean {
    return this.warrantiesForm().length > 0 || this.equipments().length > 0
  }

  public setHasWarranties(): void {
    const hasEquipmentsWarranties = this.equipments()?.some((equipment) => (equipment?.warranties ?? []).length > 0)
    const hasOrderWarranties = this.warrantiesForm()?.length > 0

    this.hasWarranties.set(hasEquipmentsWarranties || hasOrderWarranties)
  }

  public selectWarranty(warrantyForm: FormGroup<WarrantyForm>, index: number): void {
    this.selectedWarrantyForm = warrantyForm
    this.selectedWarrantyFormIndex.set(index)

    const element = document.querySelector('#order-warranty-detail') as HTMLDivElement

    if (element) {
      element.style.scale = '1.02'

      setTimeout(() => {
        element.style.scale = '1'
      }, 100)
    }
  }


  public getFirstWarranty(type: WARRANTY_TYPE): FormGroup<WarrantyForm> | null {
    this.selectedWarrantyFormIndex.set(0)
    if (type === WARRANTY_TYPE.EQUIPMENTS) {
      const warranty = this.equipments()?.find((equipment) => equipment?.warranties?.length)?.warranties?.[0] || null
      if (warranty) return this.warrantiesMapperService.createForm(warranty)
    }

    if (type === WARRANTY_TYPE.MAINTENANCES) {
      const warranties = this.warrantiesForm()
      if (warranties.length) return warranties[0]
    }

    return null
  }

  public async updateWarranty(warrantyForm: FormGroup<WarrantyForm>): Promise<void> {
    await this.warrantiesService.update(this.warrantiesMapperService.toUpdateInput(warrantyForm))
  }

  public async removeWarranty(warrantyId: string): Promise<void> {
    await this.warrantiesService.remove({ warrantyId })
  }
}
